import React, { Component } from 'react'
import {
    Button, Descriptions, Drawer, Input, message, Modal, Space, Spin, Table, Tabs, Tag, Typography
} from 'antd'
import {
    CloudServerOutlined, DatabaseOutlined, FileTextOutlined, ReloadOutlined, SearchOutlined
} from '@ant-design/icons'
import {
    getVibeAdminAppDetail,
    getVibeAdminAppHealth,
    getVibeAdminAppList,
    syncVibeAdminDatabaseReadme,
    updateVibeAdminDatabaseReadme,
} from '../../request'

const { Paragraph, Text } = Typography
const { TabPane } = Tabs

const STATUS_MAP = {
    approved: { text: '待部署', color: 'processing' },
    provisioning: { text: '接入中', color: 'processing' },
    ready: { text: '已就绪', color: 'success' },
    failed: { text: '失败', color: 'error' },
    disabled: { text: '禁用', color: 'default' },
    deleted: { text: '已删除', color: 'default' },
}

export default class VibeAppManagement extends Component {
    state = {
        dataSource: [],
        pagination: { current: 1, pageSize: 20, total: 0 },
        keyword: '',
        loading: false,
        detailVisible: false,
        detailLoading: false,
        detail: null,
        healthLoading: false,
        health: null,
        syncing: false,
        editDictionaryVisible: false,
        editDictionaryContent: '',
        editDictionarySaving: false,
    }

    componentDidMount() {
        this.loadList()
    }

    loadList = (pageNum = this.state.pagination.current, pageSize = this.state.pagination.pageSize) => {
        const { keyword } = this.state
        this.setState({ loading: true })
        return getVibeAdminAppList({ pageNum, pageSize, keyword })
            .then(res => {
                const page = res || {}
                this.setState({
                    dataSource: page.list || [],
                    pagination: {
                        current: page.pageNum || pageNum,
                        pageSize: page.pageSize || pageSize,
                        total: page.total || 0,
                    },
                })
            })
            .catch(() => {})
            .finally(() => this.setState({ loading: false }))
    }

    openDetail = (record) => {
        this.setState({ detailVisible: true, detailLoading: true, detail: null, health: null })
        getVibeAdminAppDetail({ id: record.id })
            .then(res => this.setState({ detail: res }))
            .catch(() => {})
            .finally(() => this.setState({ detailLoading: false }))
    }

    checkHealth = () => {
        const { detail } = this.state
        if (!detail?.id) return
        this.setState({ healthLoading: true })
        getVibeAdminAppHealth({ id: detail.id })
            .then(res => this.setState({ health: res }))
            .catch(() => {})
            .finally(() => this.setState({ healthLoading: false }))
    }

    syncDatabaseReadme = () => {
        const { detail } = this.state
        if (!detail?.id) return
        this.setState({ syncing: true })
        syncVibeAdminDatabaseReadme({ id: detail.id })
            .then(res => {
                message.success('数据库字典已同步')
                this.setState({ detail: res })
                this.loadList()
            })
            .catch(() => {})
            .finally(() => this.setState({ syncing: false }))
    }

    openEditDictionary = () => {
        const { detail } = this.state
        if (!detail?.id) return
        this.setState({
            editDictionaryVisible: true,
            editDictionaryContent: detail.databaseReadmeMd || '',
        })
    }

    saveDatabaseReadme = () => {
        const { detail, editDictionaryContent } = this.state
        if (!detail?.id) return
        this.setState({ editDictionarySaving: true })
        updateVibeAdminDatabaseReadme({
            id: detail.id,
            databaseReadmeMd: editDictionaryContent,
        }).then(res => {
            message.success('数据库字典已保存')
            this.setState({
                detail: res,
                editDictionaryVisible: false,
            })
            this.loadList()
        }).catch(() => {})
            .finally(() => this.setState({ editDictionarySaving: false }))
    }

    renderStatus = (status) => {
        const meta = STATUS_MAP[status] || { text: status || '-', color: 'default' }
        return <Tag color={meta.color}>{meta.text}</Tag>
    }

    renderColumns = () => [
        {
            title: '应用',
            key: 'project',
            width: 260,
            render: (_, record) => (
                <div>
                    <Space size={6} style={{ marginBottom: 6, flexWrap: 'wrap' }}>
                        <Text strong>{record.projectName || '-'}</Text>
                        <Text code style={{ fontSize: 12 }}>{record.projectSlug || '-'}</Text>
                    </Space>
                    <div><Text type="secondary">{record.description || '暂无描述'}</Text></div>
                </div>
            ),
        },
        {
            title: '所属用户',
            key: 'owner',
            width: 180,
            render: (_, record) => (
                <div>
                    <div>{record.ownerUserNick || record.ownerUserName || '-'}</div>
                    <Text type="secondary" style={{ fontSize: 12 }}>{record.ownerUserName || record.ownerUserId || '-'}</Text>
                </div>
            ),
        },
        {
            title: '状态',
            dataIndex: 'status',
            width: 100,
            render: this.renderStatus,
        },
        {
            title: '数据库',
            key: 'database',
            width: 220,
            render: (_, record) => (
                <div>
                    <Text code>{record.mysqlDatabaseName || '-'}</Text>
                    <div style={{ marginTop: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>{record.mysqlHost || '-'}:{record.mysqlPort || '-'}</Text>
                    </div>
                    <div style={{ marginTop: 4 }}>{record.mysqlDatabaseStatus ? <Tag>{record.mysqlDatabaseStatus}</Tag> : null}</div>
                </div>
            ),
        },
        {
            title: '部署',
            key: 'deploy',
            width: 240,
            render: (_, record) => (
                <div>
                    <div>{record.deployHostName || '-'}</div>
                    <Paragraph style={{ marginBottom: 0 }} ellipsis={{ rows: 1 }} copyable={record.publicUrl ? { text: record.publicUrl } : false}>
                        <Text type="secondary">{record.publicUrl || '-'}</Text>
                    </Paragraph>
                </div>
            ),
        },
        {
            title: '字典同步',
            key: 'dictionary',
            width: 190,
            render: (_, record) => (
                <div>
                    <div>{record.databaseReadmeSyncedAt || '-'}</div>
                    {record.databaseReadmeError ? <Text type="danger" style={{ fontSize: 12 }}>{record.databaseReadmeError}</Text> : null}
                </div>
            ),
        },
        {
            title: '操作',
            key: 'action',
            fixed: 'right',
            width: 120,
            render: (_, record) => (
                <Button size="small" type="primary" ghost onClick={() => this.openDetail(record)}>详情</Button>
            ),
        },
    ]

    renderDetailDrawer = () => {
        const { detailVisible, detailLoading, detail, health, healthLoading, syncing } = this.state
        return (
            <Drawer
                title={detail ? `应用详情：${detail.projectName || detail.projectSlug}` : '应用详情'}
                visible={detailVisible}
                width={900}
                onClose={() => this.setState({ detailVisible: false })}
                destroyOnClose
            >
                <Space style={{ marginBottom: 16 }} wrap>
                    <Button icon={<ReloadOutlined />} loading={healthLoading} onClick={this.checkHealth}>检查运行状态</Button>
                    <Button type="primary" ghost icon={<FileTextOutlined />} loading={syncing} onClick={this.syncDatabaseReadme}>
                        同步数据字典
                    </Button>
                </Space>
                <Spin spinning={detailLoading}>
                {detail ? (
                    <Tabs defaultActiveKey="info">
                        <TabPane tab={<span><CloudServerOutlined />应用信息</span>} key="info">
                            <Descriptions bordered column={2} size="small">
                                <Descriptions.Item label="应用名称">{detail.projectName || '-'}</Descriptions.Item>
                                <Descriptions.Item label="状态">{this.renderStatus(detail.status)}</Descriptions.Item>
                                <Descriptions.Item label="所属用户">{detail.ownerUserNick || detail.ownerUserName || '-'}</Descriptions.Item>
                                <Descriptions.Item label="GitLab 项目">
                                    {detail.gitlabWebUrl ? <a href={detail.gitlabWebUrl} target="_blank" rel="noreferrer">{detail.gitlabProjectPath || detail.gitlabWebUrl}</a> : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label="公开地址" span={2}>
                                    <Paragraph copyable={detail.publicUrl ? { text: detail.publicUrl } : false} style={{ marginBottom: 0 }}>
                                        {detail.publicUrl || '-'}
                                    </Paragraph>
                                </Descriptions.Item>
                                <Descriptions.Item label="部署服务器">{detail.deployHostName || '-'}</Descriptions.Item>
                                <Descriptions.Item label="部署端口">{detail.deployPort || '-'}</Descriptions.Item>
                                <Descriptions.Item label="部署目录" span={2}>{detail.deployPath || '-'}</Descriptions.Item>
                                <Descriptions.Item label="健康检查">{detail.healthPath || '-'}</Descriptions.Item>
                                <Descriptions.Item label="最近接入">{detail.lastProvisionedAt || '-'}</Descriptions.Item>
                            </Descriptions>
                            {health ? (
                                <div style={{ marginTop: 16, padding: 12, border: '1px solid #edf0f5', background: '#fafafa' }}>
                                    <Space style={{ marginBottom: 8 }}>
                                        <Text strong>运行状态</Text>
                                        <Tag color={health.healthy ? 'success' : 'error'}>{health.healthy ? '健康' : '异常'}</Tag>
                                        {health.statusCode ? <Tag>{health.statusCode}</Tag> : null}
                                    </Space>
                                    <Paragraph copyable={health.healthUrl ? { text: health.healthUrl } : false}>{health.healthUrl || health.message || '-'}</Paragraph>
                                    {health.body ? <pre style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{health.body}</pre> : null}
                                </div>
                            ) : null}
                        </TabPane>
                        <TabPane tab={<span><DatabaseOutlined />数据库字典</span>} key="dictionary">
                            <Space style={{ marginBottom: 12 }} wrap>
                                <Button type="primary" ghost icon={<FileTextOutlined />} onClick={this.openEditDictionary}>
                                    编辑字典
                                </Button>
                            </Space>
                            <Descriptions bordered column={2} size="small" style={{ marginBottom: 12 }}>
                                <Descriptions.Item label="数据库">{detail.mysqlDatabaseName || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Host/Port">{detail.mysqlHost || '-'}:{detail.mysqlPort || '-'}</Descriptions.Item>
                                <Descriptions.Item label="JDBC URL" span={2}>
                                    <Paragraph copyable={detail.mysqlJdbcUrl ? { text: detail.mysqlJdbcUrl } : false} style={{ marginBottom: 0 }}>
                                        {detail.mysqlJdbcUrl || '-'}
                                    </Paragraph>
                                </Descriptions.Item>
                                <Descriptions.Item label="同步时间">{detail.databaseReadmeSyncedAt || '-'}</Descriptions.Item>
                                <Descriptions.Item label="同步错误" span={2}>{detail.databaseReadmeError || '-'}</Descriptions.Item>
                            </Descriptions>
                            <pre style={{ minHeight: 260, whiteSpace: 'pre-wrap', background: '#0f172a', color: '#e5e7eb', padding: 16, overflow: 'auto' }}>
                                {detail.databaseReadmeMd || '暂无 database_readme.md 内容'}
                            </pre>
                        </TabPane>
                        <TabPane tab="日志" key="logs">
                            <Table
                                size="small"
                                rowKey="id"
                                dataSource={detail.provisionLogs || []}
                                pagination={false}
                                columns={[
                                    { title: '时间', dataIndex: 'createTime', width: 170 },
                                    { title: '步骤', dataIndex: 'stepName', width: 160 },
                                    { title: '状态', dataIndex: 'stepStatus', width: 100, render: v => <Tag>{v || '-'}</Tag> },
                                    { title: '消息', dataIndex: 'message' },
                                ]}
                            />
                        </TabPane>
                    </Tabs>
                ) : null}
                </Spin>
                {this.renderDictionaryModal()}
            </Drawer>
        )
    }

    renderDictionaryModal = () => {
        const { editDictionaryVisible, editDictionaryContent, editDictionarySaving, detail } = this.state
        return (
            <Modal
                title={`编辑数据库字典：${detail?.projectName || detail?.projectSlug || ''}`}
                visible={editDictionaryVisible}
                width={900}
                okText="保存"
                confirmLoading={editDictionarySaving}
                onOk={this.saveDatabaseReadme}
                onCancel={() => this.setState({ editDictionaryVisible: false })}
                destroyOnClose
            >
                <Input.TextArea
                    value={editDictionaryContent}
                    rows={20}
                    placeholder="可粘贴或维护 database_readme.md 内容"
                    onChange={e => this.setState({ editDictionaryContent: e.target.value })}
                />
            </Modal>
        )
    }

    render() {
        const { dataSource, pagination, loading, keyword } = this.state
        return (
            <div style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <Space>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="搜索应用、用户、数据库"
                            value={keyword}
                            onChange={e => this.setState({ keyword: e.target.value })}
                            onPressEnter={() => this.loadList(1)}
                            style={{ width: 260 }}
                        />
                        <Button type="primary" icon={<SearchOutlined />} onClick={() => this.loadList(1)}>查询</Button>
                    </Space>
                </div>
                <Table
                    rowKey="id"
                    size="middle"
                    loading={loading}
                    dataSource={dataSource}
                    columns={this.renderColumns()}
                    scroll={{ x: 1300 }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showTotal: total => `共 ${total} 个应用`,
                    }}
                    onChange={page => this.loadList(page.current, page.pageSize)}
                />
                {this.renderDetailDrawer()}
            </div>
        )
    }
}
